/**
 * Third-party stylesheets and scripts, added from JavaScript instead of index.html so none of them blocks the first
 * render: a `<link rel="stylesheet">` in the head holds back every page until it downloads, and marketing scripts
 * (HubSpot, Calendly) compete with the page's startup on phones.
 */

// Versions match what index.html loaded before (the map components were built against them).
const MAP_STYLESHEETS = [
  "https://api.mapbox.com/mapbox-gl-js/v2.6.1/mapbox-gl.css",
  "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-draw/v1.3.0/mapbox-gl-draw.css",
  "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v4.7.2/mapbox-gl-geocoder.css",
]
const CALENDLY_STYLESHEET = "https://assets.calendly.com/assets/external/widget.css"
const CALENDLY_SCRIPT = "https://assets.calendly.com/assets/external/widget.js"
const HUBSPOT_SCRIPT = "https://js-eu1.hs-scripts.com/144595535.js"

const addStylesheet = (href: string) => {
  if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return
  const link = document.createElement("link")
  link.rel = "stylesheet"
  link.href = href
  document.head.appendChild(link)
}

const scriptLoads = new Map<string, Promise<void>>()

/** Adds the script once; resolves when it has run (rejects if it fails to load, e.g. an ad blocker). */
const loadScript = (src: string, id?: string): Promise<void> => {
  const existing = scriptLoads.get(src)
  if (existing) return existing
  const loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script")
    if (id) script.id = id
    script.async = true
    script.src = src
    script.onload = () => resolve()
    script.onerror = () => {
      scriptLoads.delete(src)
      reject(new Error(`${src} failed to load`))
    }
    document.body.appendChild(script)
  })
  scriptLoads.set(src, loading)
  return loading
}

/** Map styles: added at startup (not render-blocking when added from script), long before any map is opened. */
export const addMapStylesheets = (): void => MAP_STYLESHEETS.forEach(addStylesheet)

/** Calendly's popup widget ("Book a demo"); safe to call again, e.g. on a click before the deferred load ran. */
export const loadCalendly = (): Promise<void> => {
  addStylesheet(CALENDLY_STYLESHEET)
  return loadScript(CALENDLY_SCRIPT)
}

/** HubSpot tracking and chat (the embed code HubSpot gives, as a deferred script). */
export const loadHubSpot = (): Promise<void> => loadScript(HUBSPOT_SCRIPT, "hs-script-loader")
